import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { subHours, subDays, subYears } from 'date-fns';

export interface Trigger {
  id: string;
  device_id: string;
  created_at: string;
  message: string | null;
}

export interface Device {
  device_id: string;
  name: string | null;
  hardware_type: string | null;
  last_seen: string | null;
}

export type Range = '1h' | '24h' | '7d' | '30d' | '1y' | '10y';

export function rangeToDate(range: Range): Date {
  const now = new Date();
  if (range === '1h') return subHours(now, 1);
  if (range === '24h') return subHours(now, 24);
  if (range === '7d') return subDays(now, 7);
  if (range === '30d') return subDays(now, 30);
  if (range === '1y') return subYears(now, 1);
  return subYears(now, 10);
}

const PAGE_SIZE = 1000;
const MAX_ROWS = 200000;
const CONCURRENCY = 6;

// A page is only stable if the query is fully deterministic: a fixed upper
// bound (so rows inserted mid-fetch cannot shift offsets) and a tiebreaker
// on id (so rows sharing a created_at cannot swap between pages).
function fetchPage(start: string, end: string, from: number) {
  return supabase
    .from('led_triggers')
    .select('id, device_id, created_at, message')
    .gte('created_at', start)
    .lte('created_at', end)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .range(from, from + PAGE_SIZE - 1);
}

// Streams pages back as they arrive so charts can paint before the full
// archive has downloaded. Pages are fetched in parallel batches against a
// frozen snapshot upper bound.
async function fetchAllTriggers(
  start: string,
  end: string,
  onPage: (rows: any[]) => void,
  isCancelled: () => boolean,
) {
  let offset = 0;
  while (offset < MAX_ROWS) {
    const batch = Array.from({ length: CONCURRENCY }, (_, i) => offset + i * PAGE_SIZE)
      .filter((from) => from < MAX_ROWS)
      .map((from) => fetchPage(start, end, from));
    const results = await Promise.all(batch);
    if (isCancelled()) return;
    let done = false;
    for (const { data, error } of results) {
      if (error || !data) { done = true; break; }
      if (data.length) onPage(data);
      if (data.length < PAGE_SIZE) { done = true; break; }
    }
    if (done) return;
    offset += CONCURRENCY * PAGE_SIZE;
  }
}

export function useTriggerStream(range: Range, realtime: boolean, publicEmbed = false) {
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [lastTrigger, setLastTrigger] = useState<Trigger | null>(null);
  const [loading, setLoading] = useState(true);
  // True until the full archive has finished paging in. Counters use this to
  // avoid showing a partial, ever-changing total while pages stream.
  const [streaming, setStreaming] = useState(true);
  // Authoritative row count from the database, independent of paging progress.
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const rangeRef = useRef(range);
  rangeRef.current = range;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setStreaming(true);
    setTotalCount(null);
    setTriggers([]);
    const start = rangeToDate(range).toISOString();
    // Frozen snapshot bound: rows inserted while paging arrive via realtime.
    const end = new Date().toISOString();

    const deviceQuery = publicEmbed
      ? supabase
        .from('devices')
        .select('device_id, name')
      : supabase
        .from('devices')
        .select('device_id, name, hardware_type, last_seen');

    deviceQuery.then((devRes) => {
      if (cancelled) return;
      setDevices((devRes.data || []).map((device: any) => ({
        device_id: device.device_id,
        name: device.name,
        hardware_type: 'hardware_type' in device ? device.hardware_type : null,
        last_seen: 'last_seen' in device ? device.last_seen : null,
      })) as Device[]);
    });

    supabase
      .from('led_triggers')
      .select('id', { count: 'exact', head: true })
      .not('device_id', 'is', null)
      .gte('created_at', start)
      .lte('created_at', end)
      .then(({ count }) => {
        if (!cancelled && typeof count === 'number') setTotalCount(count);
      });

    fetchAllTriggers(
      start,
      end,
      (rows) => {
        const mapped = rows
          .filter((t) => t.device_id)
          .map((t) => ({ ...t, message: publicEmbed ? null : t.message })) as Trigger[];
        setTriggers((prev) => {
          const seen = new Set(prev.map((p) => p.id));
          const fresh = mapped.filter((t) => !seen.has(t.id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
        setLoading(false);
      },
      () => cancelled,
    ).then(() => {
      if (cancelled) return;
      setLoading(false);
      setStreaming(false);
    });

    return () => {
      cancelled = true;
    };
  }, [range, publicEmbed]);



  useEffect(() => {
    if (!realtime) return;
    const channel = supabase
      .channel('viz-triggers')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'led_triggers' },
        (payload) => {
          const incoming = payload.new as Trigger;
          const t = publicEmbed ? { ...incoming, message: null } : incoming;
          if (!t.device_id) return;
          setTriggers((prev) => {
            if (prev.some((p) => p.id === t.id)) return prev;
            setTotalCount((c) => (c === null ? c : c + 1));
            return [t, ...prev];
          });
          setLastTrigger(t);
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [realtime, publicEmbed]);


  return { triggers, devices, lastTrigger, loading, streaming, totalCount };
}
